import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Subject, catchError, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { isJwtAuthError, isPublicAuthRequest, isRefreshTokenRequest } from './auth-endpoints';
import { SKIP_AUTH_ERROR } from './error.interceptor';

let isRefreshing = false;
/** Re-created after every refresh cycle so late subscribers never replay a stale value. */
let refreshSubject = new Subject<string>();

export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  if (isRefreshTokenRequest(req.url)) {
    // Confirmed live against the real dev backend: it's Flask-JWT-Extended, and
    // /auth/refresh-token requires a token with `type: "refresh"` — sending the access
    // token here gets a hard `422 {"msg":"Only refresh tokens are allowed"}`. The refresh
    // token is the only thing that works; no request body is needed either.
    const refreshToken = auth.getRefreshToken();
    if (refreshToken) {
      req = req.clone({ setHeaders: { Authorization: `Bearer ${refreshToken}` } });
    }
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // A protected JWT failure always attempts a refresh; the backend decides whether the token is valid.
      if (!isJwtAuthError(error) || isPublicAuthRequest(req.url) || isRefreshTokenRequest(req.url)) {
        return throwError(() => error);
      }

      if (isRefreshing) {
        // A refresh triggered by another request is already in flight — wait for it
        // (success or failure) instead of starting a second, redundant refresh call.
        return refreshSubject.pipe(
          take(1),
          // The request that started the refresh will perform the session-expired logout.
          catchError(() => {
            req.context.set(SKIP_AUTH_ERROR, true);
            return throwError(() => error);
          }),
          switchMap((token) => next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))),
        );
      }

      isRefreshing = true;
      const waiters = refreshSubject;

      return auth.refreshToken().pipe(
        switchMap((res) => {
          isRefreshing = false;
          refreshSubject = new Subject<string>();
          waiters.next(res.data.access_token);
          waiters.complete();
          return next(req.clone({ setHeaders: { Authorization: `Bearer ${res.data.access_token}` } }));
        }),
        catchError((refreshError: HttpErrorResponse) => {
          isRefreshing = false;
          refreshSubject = new Subject<string>();
          waiters.error(refreshError);
          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
